import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Truck, RotateCcw, Shield, Star, Minus, Plus, ChevronRight } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';
import Button from './components/Button';
import ProductCard from './components/ProductCard';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [selectedSize, setSelectedSize] = useState('M');
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const [isWishlisted, setIsWishlisted] = useState(false);

    const product = {
        id: 1,
        name: 'Chanderi Silk Saree',
        price: 8999,
        originalPrice: 11999,
        description: 'Exquisite Chanderi silk saree featuring traditional handloom weaving techniques passed down through generations. The intricate zari work and delicate motifs make this piece a true celebration of Indian textile heritage.',
        category: 'Sarees',
        images: [
            'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800',
            'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
        ],
        sizes: ['XS', 'S', 'M', 'L', 'XL'],
        details: [
            'Pure Chanderi silk fabric',
            'Handwoven zari border',
            'Traditional buttis motifs',
            'Includes matching blouse piece',
            'Dry clean only',
        ],
        rating: 4.8,
        reviews: 124,
    };

    const relatedProducts = [
        { id: 2, name: 'Banarasi Kurta Set', price: 6499, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Embroidered Anarkali', price: 12999, tag: 'NEW', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Handloom Cotton Dupatta', price: 2499, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Silk Blend Lehenga', price: 18500, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <div className="pt-20 md:pt-24">
                {/* Breadcrumb */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-2 text-xs text-[#64748B]">
                        <button onClick={() => navigate('/template9')} className="hover:text-[#2563EB] bg-transparent border-none cursor-pointer text-xs text-[#64748B]">Home</button>
                        <ChevronRight size={12} />
                        <button onClick={() => navigate('/template9/shop')} className="hover:text-[#2563EB] bg-transparent border-none cursor-pointer text-xs text-[#64748B]">Shop</button>
                        <ChevronRight size={12} />
                        <span className="text-[#0F172A] font-medium">{product.name}</span>
                    </div>
                </div>

                {/* Product Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
                        {/* Image Gallery */}
                        <div>
                            <div className="relative aspect-[3/4] overflow-hidden rounded-none border border-[#E2E8F0] mb-4">
                                <img
                                    src={product.images[activeImage]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#2563EB]" />
                                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#2563EB]" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveImage(idx)}
                                        className={`aspect-square overflow-hidden rounded-none border-2 transition-colors duration-300 ${
                                            activeImage === idx ? 'border-[#2563EB]' : 'border-[#E2E8F0]'
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Product Info */}
                        <div className="lg:pt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-0.5 bg-[#2563EB]" />
                                <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">{product.category}</p>
                            </div>
                            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4">
                                {product.name}
                            </h1>

                            {/* Rating */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="flex gap-0.5">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-[#2563EB] text-[#2563EB]' : 'text-[#E2E8F0]'}`} />
                                    ))}
                                </div>
                                <span className="text-sm text-[#64748B]">{product.rating} ({product.reviews} reviews)</span>
                            </div>

                            {/* Price */}
                            <div className="flex items-baseline gap-3 mb-6">
                                <span className="text-2xl sm:text-3xl font-bold text-[#2563EB]">
                                    {'\u20B9'}{product.price.toLocaleString('en-IN')}
                                </span>
                                {product.originalPrice && (
                                    <span className="text-lg text-[#64748B] line-through">
                                        {'\u20B9'}{product.originalPrice.toLocaleString('en-IN')}
                                    </span>
                                )}
                                {product.originalPrice && (
                                    <span className="text-sm font-medium text-green-600">
                                        {Math.round((1 - product.price / product.originalPrice) * 100)}% off
                                    </span>
                                )}
                            </div>

                            <p className="text-sm text-[#64748B] leading-relaxed mb-8 font-light">
                                {product.description}
                            </p>

                            {/* Size Selection */}
                            <div className="mb-6">
                                <p className="text-sm font-semibold text-[#0F172A] mb-3 uppercase tracking-wider">Size</p>
                                <div className="flex gap-2">
                                    {product.sizes.map((size) => (
                                        <button
                                            key={size}
                                            onClick={() => setSelectedSize(size)}
                                            className={`w-12 h-12 flex items-center justify-center text-sm font-medium rounded-none border-2 transition-all duration-300 ${
                                                selectedSize === size
                                                    ? 'border-[#2563EB] bg-[#2563EB] text-white'
                                                    : 'border-[#E2E8F0] text-[#0F172A] hover:border-[#2563EB]'
                                            }`}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="mb-8">
                                <p className="text-sm font-semibold text-[#0F172A] mb-3 uppercase tracking-wider">Quantity</p>
                                <div className="flex items-center border border-[#E2E8F0] rounded-none w-fit">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="w-10 h-10 flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                                    >
                                        <Minus size={16} className="text-[#64748B]" />
                                    </button>
                                    <span className="w-12 h-10 flex items-center justify-center text-sm font-medium text-[#0F172A] border-l border-r border-[#E2E8F0]">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-10 h-10 flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-[#F8FAFC] transition-colors"
                                    >
                                        <Plus size={16} className="text-[#64748B]" />
                                    </button>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                <Button variant="primary" size="lg" fullWidth icon={ShoppingCart}>
                                    Add to Cart
                                </Button>
                                <Button
                                    variant="secondary"
                                    size="lg"
                                    onClick={() => setIsWishlisted(!isWishlisted)}
                                    icon={Heart}
                                >
                                    {isWishlisted ? 'Wishlisted' : 'Wishlist'}
                                </Button>
                            </div>

                            {/* Trust Badges */}
                            <div className="grid grid-cols-3 gap-4 py-6 border-t border-[#E2E8F0]">
                                <div className="flex flex-col items-center text-center">
                                    <Truck size={18} className="text-[#2563EB] mb-1" />
                                    <span className="text-[10px] sm:text-xs text-[#64748B]">Free Shipping</span>
                                </div>
                                <div className="flex flex-col items-center text-center">
                                    <RotateCcw size={18} className="text-[#2563EB] mb-1" />
                                    <span className="text-[10px] sm:text-xs text-[#64748B]">7-Day Returns</span>
                                </div>
                                <div className="flex flex-col items-center text-center">
                                    <Shield size={18} className="text-[#2563EB] mb-1" />
                                    <span className="text-[10px] sm:text-xs text-[#64748B]">Authentic</span>
                                </div>
                            </div>

                            {/* Product Details */}
                            <div className="py-6 border-t border-[#E2E8F0]">
                                <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider mb-4">Product Details</h3>
                                <ul className="space-y-2">
                                    {product.details.map((detail, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm text-[#64748B] font-light">
                                            <div className="w-1.5 h-1.5 bg-[#2563EB] mt-1.5 flex-shrink-0" />
                                            {detail}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Related Products */}
                    <div className="mt-16 lg:mt-24">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-0.5 bg-[#2563EB]" />
                            <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">You May Also Like</p>
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-[#0F172A] mb-8">Related Products</h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {relatedProducts.map((p) => (
                                <ProductCard key={p.id} product={p} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetail;
